import { useState } from 'react';
import type { KeenSliderInstance, KeenSliderOptions, KeenSliderPlugin } from 'keen-slider/react';
import { TrackDetails, useKeenSlider } from 'keen-slider/react';

import useEvent from '@lyearn/core/hooks/useEvent';

import { CarouselProps, CarouselState } from '../../types';

const getTrackDetailsPageInfo = (trackDetails: TrackDetails, totalCount: number) => {
  const { maxIdx, slides } = trackDetails;

  const slideVisible = Math.max(totalCount - maxIdx, 1);
  const totalPages = Math.ceil(totalCount / slideVisible);

  const currentActiveSlideIndex = slides.findIndex((slide) => slide.portion >= 0.9);
  let activePage = Math.ceil(currentActiveSlideIndex / slideVisible);
  if (currentActiveSlideIndex === maxIdx) {
    activePage = totalPages - 1;
  }

  return { totalPages, activePage, slideVisible };
};

const useCarousel = (
  props: Pick<
    CarouselProps,
    | 'navigation'
    | 'showShadows'
    | 'pagination'
    | 'itemWidth'
    | 'speed'
    | 'totalCount'
    | 'slidesPerView'
    | 'onCreate'
    | 'slidesPerGroupAuto'
    | 'spaceBetween'
    | 'initialPage'
    | 'onPageChange'
  > & {
    keenSliderProps?: {
      options?: Partial<KeenSliderOptions>;
      plugins?: KeenSliderPlugin[];
    };
  },
) => {
  const {
    initialPage,
    navigation,
    showShadows,
    spaceBetween: spacing,
    slidesPerView,
    speed,
    totalCount,
    onCreate,
    itemWidth,
    slidesPerGroupAuto,
    pagination,
    keenSliderProps,
    onPageChange: onPageChangeProp,
  } = props;

  const [carouselState, setCarouselState] = useState<CarouselState>({
    totalPages: 0,
    activePage: 0,
    showLeftArrow: false,
    showRightArrow: false,
  });

  const onDetailsChanged = useEvent((slider: KeenSliderInstance) => {
    onPageChangeProp?.(slider.track.details.abs + 1);
    setCarouselState((prevState) => {
      let nextState = prevState;

      if (navigation || showShadows) {
        const { progress } = slider.track.details;
        const showLeftArrow = progress > 0.01;
        const showRightArrow = progress < 0.99;

        if (
          showLeftArrow !== prevState.showLeftArrow ||
          showRightArrow !== prevState.showRightArrow
        ) {
          nextState = { ...nextState, showLeftArrow, showRightArrow };
        }
      }

      if (pagination && (typeof itemWidth === 'number' || typeof slidesPerView === 'number')) {
        const { totalPages, activePage } = getTrackDetailsPageInfo(
          slider.track.details,
          totalCount,
        );

        if (totalPages !== prevState.totalPages || activePage !== prevState.activePage) {
          nextState = { ...nextState, totalPages, activePage };
        }
      }

      return nextState;
    });

    if (itemWidth === 'auto') {
      const containerWidth = slider.container.offsetWidth;

      slider.slides.forEach((slide, idx) => {
        slide.style.left = slider.track.details.slides[idx].distance * containerWidth + 'px';
      });
    }
  });

  const [sliderRef, keenSliderRef] = useKeenSlider(
    {
      defaultAnimation: { duration: speed },
      slides: { number: totalCount, perView: slidesPerView, spacing },
      renderMode: itemWidth === 'auto' ? 'custom' : 'performance',
      initial: initialPage,
      created: onCreate,
      detailsChanged: onDetailsChanged,
      ...keenSliderProps?.options,
    },
    keenSliderProps?.plugins,
  );

  const onLeftArrowClick = useEvent(() => {
    if (!keenSliderRef.current) {
      return;
    }

    if (slidesPerGroupAuto) {
      const { distToIdx, details } = keenSliderRef.current.track;
      const index = distToIdx(-1) || details.minIdx;
      keenSliderRef.current.moveToIdx(index);
    } else {
      keenSliderRef.current.prev();
    }
  });

  const onRightArrowClick = useEvent(() => {
    if (!keenSliderRef.current) {
      return;
    }

    if (slidesPerGroupAuto) {
      const { distToIdx, details } = keenSliderRef.current.track;
      // find the first slide in next page or incomplete slide of current page
      const slide = details.slides.find((slide) => slide.distance > 0 && slide.portion < 0.9);
      const index = distToIdx(slide?.distance ?? 1) || details.maxIdx;
      keenSliderRef.current.moveToIdx(index);
    } else {
      keenSliderRef.current.next();
    }
  });

  const onPageChange = useEvent((pageIndex: number) => {
    if (keenSliderRef.current) {
      const { details } = keenSliderRef.current.track;
      const { slideVisible, totalPages } = getTrackDetailsPageInfo(details, totalCount);
      if (totalPages === pageIndex + 1) {
        keenSliderRef.current.moveToIdx(details.maxIdx);
      } else {
        keenSliderRef.current.moveToIdx(pageIndex * slideVisible);
      }
    }
  });

  return {
    onPageChange,
    onRightArrowClick,
    onLeftArrowClick,
    sliderRef,
    carouselState,
    keenSliderRef,
  };
};

export default useCarousel;
